import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowRight, Shield, Search, MessageCircle, Star, Users, Clock, Check } from 'lucide-react';

const GoFella = () => {
  const keyFeatures = [
    {
      icon: <Shield className="h-6 w-6" />,
      title: "ABN & Licence Verification",
      description: "All tradies verified with ABN, licence, and insurance checks for your peace of mind"
    },
    {
      icon: <Search className="h-6 w-6" />,
      title: "Smart Search & Filter",
      description: "Find local tradies by location, trade type, rating, and availability"
    },
    {
      icon: <MessageCircle className="h-6 w-6" />,
      title: "Secure Messaging",
      description: "Chat directly with tradies through our secure in-app messaging system"
    },
    {
      icon: <Star className="h-6 w-6" />,
      title: "Ratings & Reviews",
      description: "Read genuine reviews and see ratings from verified customers"
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Tradie Profiles",
      description: "Detailed profiles with portfolios, service areas, and pricing indications"
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: "Quick Quote Requests",
      description: "Get quotes fast by describing your job and connecting with available tradies"
    }
  ];

  const whoItsFor = [
    {
      title: "Homeowners",
      description: "Finding reliable tradies for home repairs, renovations, and maintenance projects"
    },
    {
      title: "Small Businesses", 
      description: "Connecting with trusted tradies for office fit-outs, repairs, and ongoing maintenance"
    },
    {
      title: "Property Managers",
      description: "Managing multiple properties with access to verified tradies across different locations"
    },
    {
      title: "Tradies",
      description: "Growing your business with more visibility and direct connections to customers"
    }
  ];

  const futureFeatures = [
    "Online booking and scheduling system",
    "Integrated payment processing",
    "Job management dashboard for tradies",
    "Premium subscription tiers with enhanced features",
    "Warranty tracking and follow-up services"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-vincotek-blue to-blue-700 text-white py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              GoFella
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto leading-relaxed">
              Connecting Australians with Trusted Tradies
            </p>
            <p className="text-lg text-blue-200 max-w-2xl mx-auto">
              GoFella helps homeowners and businesses find verified tradies quickly and safely, making it easier than ever to get quality work done.
            </p>
          </div>
        </div>
      </section>

      {/* What is GoFella Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-8">
              What is GoFella?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-12">
              GoFella is a digital platform that revolutionizes how Australians connect with trusted tradies. Whether you need a plumber for an emergency repair, an electrician for a home renovation, or a handyman for general maintenance, GoFella makes it simple to discover local tradies, view their profiles with ratings and reviews, and request quotes online. Our comprehensive verification system ensures you're always connecting with legitimate, qualified professionals.
            </p>

            {/* Platform Preview */}
            <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl p-8 mb-12">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="w-16 h-16 bg-vincotek-blue rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-vincotek-dark mb-2">Discover</h3>
                  <p className="text-gray-600 text-sm">Find local tradies by location and trade type</p>
                </div>
                <div>
                  <div className="w-16 h-16 bg-vincotek-yellow rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageCircle className="h-8 w-8 text-vincotek-dark" />
                  </div>
                  <h3 className="text-lg font-semibold text-vincotek-dark mb-2">Connect</h3>
                  <p className="text-gray-600 text-sm">View profiles, ratings, and request quotes</p>
                </div>
                <div>
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-vincotek-dark mb-2">Get Work Done</h3>
                  <p className="text-gray-600 text-sm">Hire verified tradies with confidence</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-12 text-center">
              Key Features
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {keyFeatures.map((feature, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                  <CardContent className="p-6">
                    <div className="text-vincotek-blue mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-vincotek-dark mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who GoFella is For Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-12 text-center">
              Who GoFella is For
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {whoItsFor.map((audience, index) => (
                <Card key={index} className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-vincotek-dark mb-4">
                      {audience.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {audience.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-lg text-gray-600 leading-relaxed">
                <strong>For everyday users</strong>, GoFella provides a reliable way to find and hire qualified tradies for any job, big or small. <strong>For tradies</strong>, it offers increased visibility, direct customer connections, and tools to grow their business in the digital age.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Vision Section */}
      <section className="py-20 bg-gradient-to-br from-vincotek-yellow/10 to-yellow-100/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-8">
              Our Vision for GoFella
            </h2>
            <div className="space-y-6 mb-12">
              <p className="text-lg text-gray-700 leading-relaxed">
                GoFella is currently in its early development stage as we work to create the most comprehensive and user-friendly platform for connecting Australians with trusted tradies. Our vision is to transform the traditional process of finding reliable trade services by leveraging technology to create transparency, trust, and convenience.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We're building more than just a directory – we're creating an ecosystem that benefits both customers and tradies through verified profiles, streamlined communication, and tools that help grow local businesses while ensuring customers get quality service every time.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-vincotek-dark mb-6">Planned Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {futureFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-vincotek-blue rounded-full flex-shrink-0"></div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Stay Updated on GoFella
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Be the first to know when GoFella launches. Register your interest and we'll keep you updated on our progress.
          </p>
          <Link to="/contact">
            <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
              Register Your Interest
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default GoFella;